import useActionColumn from './utility/useActionColumn';
import {
  GRID_MODE,
  DEFAULT_TOOLBAR,
  DEFAULT_SEARCH,
  DEFAULT_PAGINATION,
  DEFAULT_INITIAL_PINNED_COLUMNS,
  DEFAULT_PERSIST_GRID_STATE,
  DEFAULT_SERVER_SIDE,
  DEFAULT_GET_ROW_CLASS_NAME,
  DEFAULT_ACTIONS,
  DEFAULT_LAYOUT_SYNC,
} from './const/dataGridConstants.js';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box } from '@mui/material';
import CustomToolbar from './components/CustomToolbar';
import NoDataOverlay from './components/NoDataOverlay';
import useLocalStorageGridState from './hooks/useLocalStorageGridState';
import useExpandableRows from './hooks/useExpandableRows';
import useServerGridLayout from './hooks/useServerGridLayout';
import FormSpinner from 'components/FormSpinner';
import { DataGridPro } from '@mui/x-data-grid-pro';
import useCommonDataGrid from './hooks/useCommonDataGrid.js';

const CommonDataGrid = ({
  rows = [],
  columns = [],
  loading = false,
  className = '',
  mode = GRID_MODE.CLIENT,
  paginationMode = mode,
  toolbar = DEFAULT_TOOLBAR,
  search = DEFAULT_SEARCH,
  enableColumnSearch = false,
  disableColumnReorder = true,
  pagination = DEFAULT_PAGINATION,
  initialPinnedColumns = DEFAULT_INITIAL_PINNED_COLUMNS,
  checkboxSelection = false,
  disableColumnMenu = false,
  disableColumnResize = true,
  persistGridState = DEFAULT_PERSIST_GRID_STATE,
  storageKey = 'common-grid',
  serverSide = DEFAULT_SERVER_SIDE,
  onRowClick,
  getRowId,
  getRowClassName = DEFAULT_GET_ROW_CLASS_NAME,
  actions = DEFAULT_ACTIONS,
  layoutSync = DEFAULT_LAYOUT_SYNC,
  exportActions = [],
  onSelectionChange,
  selectionModel: controlledSelectionModel,
  expandableRows,
  ...otherProps
}) => {
  const paginationConfig = useMemo(() => ({ ...DEFAULT_PAGINATION, ...pagination }), [pagination]);
  const [filterLinkOperator, setFilterLinkOperator] = useState('and');
  const detailPanelProps = useExpandableRows(expandableRows);
  const isServerPagination = paginationMode === GRID_MODE.SERVER;
  const onServerStateChange = serverSide?.onStateChange;

  const {
    pageSize,
    setPageSize,
    density,
    setDensity,
    columnVisibilityModel,
    setColumnVisibilityModel,
    columnWidths,
    setColumnWidths,
    pinnedColumns,
    setPinnedColumns,
    sortModel,
    setSortModel,
    searchText,
    setSearchText,
    columnSearch,
    setColumnSearch,
    filters,
    setFilters,
    columnOrder,
    setColumnOrder,
  } = useLocalStorageGridState({
    storageKey,
    persistGridState,
    defaultPageSize: paginationConfig.pageSize,
  });

  const gridStateForSync = useMemo(
    () => ({
      columnOrder,
      setColumnOrder,
      columnVisibilityModel,
      setColumnVisibilityModel,
      columnWidths,
      setColumnWidths,
      pinnedColumns,
      setPinnedColumns,
      sortModel,
      setSortModel,
      density,
      setDensity,
      pageSize,
      setPageSize,
    }),
    [
      columnOrder,
      setColumnOrder,
      columnVisibilityModel,
      setColumnVisibilityModel,
      columnWidths,
      setColumnWidths,
      pinnedColumns,
      setPinnedColumns,
      sortModel,
      setSortModel,
      density,
      setDensity,
      pageSize,
      setPageSize,
    ]
  );

  const {
    loaded: layoutLoaded,
    saving: layoutSaving,
    isDirty: layoutDirty,
    saveLayout,
    resetLayout,
  } = useServerGridLayout({
    enabled: layoutSync.enabled,
    gridKey: layoutSync.gridKey,
    gridState: gridStateForSync,
    defaultPageSize: paginationConfig.pageSize,
  });

  const [searchInput, setSearchInput] = useState(searchText);
  const [localColumnSearch, setLocalColumnSearch] = useState(columnSearch);
  const [page, setPage] = useState(0);
  const [localSelectionModel, setSelectionModel] = useState([]);
  const selectionModel = controlledSelectionModel ?? localSelectionModel;
  const [reorderEnabled, setReorderEnabled] = useState(!disableColumnReorder);

  const hasActions =
    actions?.edit?.visible ||
    actions?.delete?.visible ||
    actions?.custom?.some((action) => action.visible !== false);

  const lastSyncedDensityRef = useRef(density);
  const actionColumn = useActionColumn(actions);

  const {
    handlePageSizeChange,
    handleColumnWidthChange,
    handleColumnOrderChange,
    handlePinnedColumnsChange,
    handleStateChange,
    orderedColumns,
    defaultPinnedColumns,
    processedRows,
    mergedColumnVisibilityModel,
    toolbarProps,
  } = useCommonDataGrid({
    setPageSize,
    setPage,
    searchInput,
    setSearchText,
    setColumnWidths,
    setLocalColumnSearch,
    setColumnOrder,
    checkboxSelection,
    initialPinnedColumns,
    hasActions,
    setPinnedColumns,
    setDensity,
    lastSyncedDensityRef,
    columns,
    columnWidths,
    disableColumnMenu,
    actionColumn,
    enableColumnSearch,
    reorderEnabled,
    localColumnSearch,
    columnOrder,
    rows,
    searchText,
    columnSearch,
    columnVisibilityModel,
    mode,
    toolbar,
    setSearchInput,
    search,
    layoutSync,
    layoutSaving,
    layoutDirty,
    saveLayout,
    resetLayout,
    setReorderEnabled,
    setColumnVisibilityModel,
    exportActions,
  });

  useEffect(() => {
    setPage(0);
  }, [searchText, columnSearch, filters, filterLinkOperator]);

  useEffect(() => {
    setSearchInput(searchText);
  }, [searchText]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setColumnSearch(localColumnSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [localColumnSearch, setColumnSearch]);

  useEffect(() => {
    if (controlledSelectionModel !== undefined) return;
    onSelectionChange?.(
      processedRows.filter((row) => selectionModel.includes(getRowId ? getRowId(row) : row.id))
    );
  }, [selectionModel, processedRows, onSelectionChange, getRowId, controlledSelectionModel]);

  useEffect(() => {
    const isEmpty = pinnedColumns.left?.length === 0 && pinnedColumns.right?.length === 0;

    if (isEmpty) {
      setPinnedColumns(defaultPinnedColumns);
    }
  }, [defaultPinnedColumns, pinnedColumns, setPinnedColumns]);

  useEffect(() => {
    if ((mode !== GRID_MODE.SERVER && !isServerPagination) || !onServerStateChange) {
      return;
    }

    onServerStateChange({
      searchText,
      page,
      pageSize,
      sortModel,
      filterModel: {
        items: filters,
        linkOperator: filterLinkOperator,
      },
      columnSearch,
    });
  }, [
    mode,
    isServerPagination,
    onServerStateChange,
    searchText,
    page,
    pageSize,
    sortModel,
    filters,
    columnSearch,
    filterLinkOperator,
  ]);

  const isEmpty = !loading && processedRows.length === 0;
  const showToolbar = toolbar?.showToolbar !== false;

  return (
    <Box className={`common-data-grid-container ${className}`}>
      {layoutSync.enabled && (!layoutLoaded || layoutSaving) && <FormSpinner />}
      <DataGridPro
        className={`common-data-grid ${isEmpty ? 'common-data-grid--empty' : ''}`}
        rows={processedRows}
        columns={orderedColumns}
        getRowId={getRowId}
        getRowClassName={getRowClassName}
        pinnedColumns={pinnedColumns}
        onColumnOrderChange={handleColumnOrderChange}
        onPinnedColumnsChange={handlePinnedColumnsChange}
        loading={loading}
        headerHeight={enableColumnSearch ? 80 : 50}
        checkboxSelection={checkboxSelection}
        selectionModel={selectionModel}
        onSelectionModelChange={(newSelection) => {
          setSelectionModel(newSelection);
          const selectedRows = processedRows.filter((row) =>
            newSelection.includes(getRowId ? getRowId(row) : row.id)
          );
          onSelectionChange?.(selectedRows);
        }}
        disableSelectionOnClick={true}
        onRowClick={onRowClick}
        density={density}
        onStateChange={handleStateChange}
        disableColumnResize={disableColumnResize}
        disableColumnReorder={!reorderEnabled}
        onColumnWidthChange={handleColumnWidthChange}
        columnVisibilityModel={mergedColumnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        sortingMode={mode === GRID_MODE.SERVER ? GRID_MODE.SERVER : GRID_MODE.CLIENT}
        pagination={paginationConfig.enabled}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
        rowsPerPageOptions={paginationConfig.rowsPerPageOptions}
        rowCount={isServerPagination ? serverSide.totalRecords : undefined}
        paginationMode={paginationMode}
        filterMode={mode === GRID_MODE.SERVER ? GRID_MODE.SERVER : GRID_MODE.CLIENT}
        filterModel={{
          items: filters,
          linkOperator: filterLinkOperator,
        }}
        onFilterModelChange={(model) => {
          setFilters(model.items || []);
          setFilterLinkOperator(model.linkOperator || 'and');
        }}
        components={{
          Toolbar: showToolbar ? CustomToolbar : null,
          NoRowsOverlay: NoDataOverlay,
        }}
        componentsProps={{
          toolbar: toolbarProps,
          pagination: {
            SelectProps: {
              MenuProps: {
                // Keep the menu within a containing dialog's focus trap.
                disablePortal: true,
                disableEnforceFocus: true,
              },
            },
          },
          panel: {
            className: 'rms-common-data-grid-panel',
            disablePortal: true,
          },
        }}
        {...detailPanelProps}
        {...otherProps}
      />
    </Box>
  );
};

export default CommonDataGrid;
